/**
 * Turning a notebook's SOURCES into the fields a query is filtered by.
 *
 * A notebook says what it contains. A query says what to narrow to. Those are not the same
 * sentence, and translating carelessly between them is how a scope silently widens.
 *
 * THE RULE, and it is one rule: `collection_ids` and `document_ids` are NARROWINGS. So a
 * notebook that contains the whole corpus must send NEITHER — the corpus is already
 * everything, and sending a document filter beside it would cut the notebook down to those
 * documents, which is the opposite of what the user added.
 *
 * This is the mistake the deleted conversation model made, for its own good reasons: it
 * dropped `collection_ids` whenever Pustaka was on, because for a conversation "collections
 * plus Pustaka" IS the whole corpus. Ported naively to notebooks that drops the collection
 * filter while KEEPING the document filter, so "all of Pustaka and these two collections"
 * quietly becomes "only these two documents", and nothing on screen looks wrong. The model
 * is gone; the trap it encoded is what these tests exist to keep out.
 *
 * Exclusions are the exception, and deliberately: "everything except these three" is the
 * one narrowing that composes with the whole corpus, and it is why exclusions are stored as
 * exclusions rather than as a list of survivors — 3,331 ids per turn would freeze the scope
 * against a corpus that keeps growing.
 */

import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";
import type { Notebook, NotebookSourceIssue, QueryRequest } from "./api-types.ts";

export interface NotebookScope {
  /** All of Pustaka — the organisation's whole corpus, LIVE. */
  wholeCorpus: boolean;
  /** Collections added whole. Live too: a document added to one later is in the notebook. */
  collectionIds: string[];
  /** Individually picked documents. */
  documentIds: string[];
  /** Documents removed from inside something added whole. */
  excludedDocumentIds: string[];
}

export const EMPTY_NOTEBOOK_SCOPE: NotebookScope = {
  wholeCorpus: false,
  collectionIds: [],
  documentIds: [],
  excludedDocumentIds: [],
};

export interface QueryScopeFields {
  collection_ids?: string[];
  document_ids?: string[];
  excluded_document_ids?: string[];
}

/**
 * Whether this notebook can be asked anything at all.
 *
 * Exclusions alone do NOT count: "except these three" with nothing to except them from is
 * still nothing. A new notebook is empty and must refuse rather than fall back to the whole
 * corpus, which would answer from somewhere the user never chose.
 */
export function isEmptyNotebook(scope: NotebookScope): boolean {
  return !scope.wholeCorpus && scope.collectionIds.length === 0 && scope.documentIds.length === 0;
}

export function notebookScopeToQueryFields(scope: NotebookScope): QueryScopeFields {
  const fields: QueryScopeFields = {};
  // Exclusions ride along with every shape: they are the one narrowing that means the same
  // thing whether the notebook holds everything or four documents.
  if (scope.excludedDocumentIds.length > 0) {
    fields.excluded_document_ids = [...scope.excludedDocumentIds];
  }

  // THE WHOLE CORPUS IS NOT NARROWED. Sending either filter here is the silent-narrowing
  // bug this module exists to prevent.
  if (scope.wholeCorpus) return fields;

  if (scope.collectionIds.length > 0) fields.collection_ids = [...scope.collectionIds];
  if (scope.documentIds.length > 0) fields.document_ids = [...scope.documentIds];
  return fields;
}

/** The request body for `PUT /notebooks/{id}/sources`. Replace semantics — see the route. */
export function notebookScopeToRequest(scope: NotebookScope): {
  whole_corpus: boolean;
  collection_ids: string[];
  documents: Array<{ document_id: string }>;
  excluded_document_ids: string[];
} {
  return {
    whole_corpus: scope.wholeCorpus,
    collection_ids: [...scope.collectionIds],
    // Ids only. The stable key that survives a re-ingest is read server-side from its own
    // row — sending one from here would make the stored record evidence of this request
    // rather than of the corpus.
    documents: scope.documentIds.map((document_id) => ({ document_id })),
    excluded_document_ids: [...scope.excludedDocumentIds],
  };
}

export function toggleWholeCorpus(scope: NotebookScope): NotebookScope {
  return { ...scope, wholeCorpus: !scope.wholeCorpus };
}

export function toggleCollection(scope: NotebookScope, id: string): NotebookScope {
  const has = scope.collectionIds.includes(id);
  return {
    ...scope,
    collectionIds: has
      ? scope.collectionIds.filter((value) => value !== id)
      : [...scope.collectionIds, id],
  };
}

export function toggleDocument(scope: NotebookScope, id: string): NotebookScope {
  const has = scope.documentIds.includes(id);
  return {
    ...scope,
    documentIds: has ? scope.documentIds.filter((value) => value !== id) : [...scope.documentIds, id],
  };
}

/**
 * Exclude a document that arrived inside something added whole.
 *
 * Removing it from `documentIds` would not work: it was never listed there, it came in with
 * a collection. So it is recorded as an exclusion, which keeps the collection live — later
 * additions still appear, and this one stays out.
 */
export function toggleExcluded(scope: NotebookScope, id: string): NotebookScope {
  const has = scope.excludedDocumentIds.includes(id);
  return {
    ...scope,
    excludedDocumentIds: has
      ? scope.excludedDocumentIds.filter((value) => value !== id)
      : [...scope.excludedDocumentIds, id],
  };
}

/** How many sources a reader sees. A collection is ONE, whatever it holds. */
export function sourceCount(scope: NotebookScope): number {
  return scope.collectionIds.length + scope.documentIds.length + (scope.wholeCorpus ? 1 : 0);
}

/** The issues worth putting in front of a reader, split by what they mean. */
export function summariseIssues(issues: NotebookSourceIssue[]): {
  moved: number;
  missing: number;
} {
  return {
    moved: issues.filter((issue) => issue.kind === "moved").length,
    missing: issues.filter((issue) => issue.kind === "missing").length,
  };
}

/** What the server says the notebook currently holds, as the client's working scope. */
export function notebookScopeFromResponse(notebook: Notebook): NotebookScope {
  return {
    wholeCorpus: notebook.whole_corpus,
    collectionIds: [...notebook.collection_ids],
    documentIds: [...notebook.document_ids],
    excludedDocumentIds: [...notebook.excluded_document_ids],
  };
}

/**
 * Whether two source sets differ in any way that must be saved.
 *
 * Order is NOT significant — the server stores what it RESOLVED and may return a different
 * order than it was sent. Comparing order-sensitively made every load look like a change,
 * which meant a PUT on every render.
 */
export function sameNotebookScope(a: NotebookScope, b: NotebookScope): boolean {
  const set = (values: string[]) => [...values].sort().join(" ");
  return (
    a.wholeCorpus === b.wholeCorpus &&
    set(a.collectionIds) === set(b.collectionIds) &&
    set(a.documentIds) === set(b.documentIds) &&
    set(a.excludedDocumentIds) === set(b.excludedDocumentIds)
  );
}

/**
 * The exact body a question is sent with.
 *
 * Extracted from the component so it can be TESTED. Nothing in CI proved the notebook's
 * sources reached the request — the live journey harness did, but it needs a running stack,
 * real models and a judge key, so it cannot gate a pull request. A regression here is
 * invisible to every gate and silent to the user: the answer simply comes from a wider or
 * narrower corpus than they chose, with no error.
 */
export function buildQueryBody({
  question,
  userId,
  conversationId,
  scope,
}: {
  question: string;
  userId: string;
  conversationId: string | null;
  scope: NotebookScope;
}): QueryRequest {
  return {
    query: question,
    user_id: userId,
    conversation_id: conversationId,
    ...notebookScopeToQueryFields(scope),
  };
}

/**
 * A name for a notebook, derived from what it holds.
 *
 * Every notebook was called "Notebook tanpa judul", so a rail of them was unusable — you
 * could not tell which was which, and switching between them is a thing the product asks
 * you to do. A name taken from the sources is meaningful the moment it exists, which a
 * random one never is.
 *
 * Deliberately NOT the first question: a notebook is created when its sources are chosen,
 * which happens BEFORE anything is asked. Titling from the question needs a rename
 * endpoint that does not exist yet.
 *
 * Returns `undefined` when there is nothing to name it after, so the caller sends no title
 * and the server keeps its own default rather than being handed a made-up one.
 */
export function notebookTitleFrom(
  scope: NotebookScope,
  collectionNames: Map<string, string>,
  documentTitles: Map<string, string>,
): string | undefined {
  if (scope.wholeCorpus) return "Seluruh Pustaka Aceh";

  const named = scope.collectionIds.map((id) => collectionNames.get(id)).filter(Boolean) as string[];
  if (named.length === 1 && scope.documentIds.length === 0) return named[0];
  if (named.length >= 1) {
    const rest = named.length - 1 + scope.documentIds.length;
    return rest > 0 ? `${named[0]} +${rest}` : named[0];
  }

  if (scope.documentIds.length === 1) {
    const title = documentTitles.get(scope.documentIds[0]!);
    // The legal long-title is unusable as a name; the label ("Qanun … 4/2013") is not.
    if (title) return truncateAtWord(parseLegalTitle(title).label || title, 60);
  }
  if (scope.documentIds.length > 1) return `${scope.documentIds.length} dokumen`;
  return undefined;
}
